import { alpha, Box, Button, LinearProgress, Paper, Slide, Typography } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { isGuest } from '../../../../helpers'
import { useCountdown } from '../../../../hooks/useCountdown'
import { useScreenSize } from '../../../../hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import { Challenge } from '../../../RewardService/types'
import { Reward } from './Reward'

const normalise = (value, threshold) => {
    const normalisedValue = (value * 100) / threshold

    return Math.min(100, normalisedValue)
}

export const ChallengeCard: FC<Challenge> = (challenge) => {
    const { title, threshold, progress, tooltip, description, assetUrl, endDate, featurePath } = challenge
    const navigate = useNavigate()
    const { isMobile } = useScreenSize()
    const [hidden, setHidden] = useState(false)
    const claimChallenge = useStoreActions((state) => state.rewardsService.claimChallenge)
    const userData = useStoreState((state) => state.userData)

    const guest = isGuest(userData.userData?.groups)

    const isClaimable = progress >= threshold

    useEffect(() => {
        if (isClaimable) {
            setTimeout(() => {
                document.getElementById(`challenge-${challenge.id}`)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center',
                })
            }, 500)
        }
    }, [isClaimable])

    const callback = useCallback(() => {
        !hidden && setHidden(true)
    }, [hidden])

    const handleClaim = useCallback(async () => {
        !guest && claimChallenge(challenge.id)
    }, [challenge.id, guest])

    const timeLeft = useCountdown(endDate, callback)

    const headerTypographyVariant = isMobile ? 'h6' : 'h5'

    const color = isClaimable ? 'secondary' : 'primary'

    if (hidden) return null

    return (
        <Slide direction="right" in={!hidden}>
            <Paper
                onClick={() => {
                    if (isClaimable) {
                        handleClaim()
                    } else {
                        if (featurePath) {
                            navigate(featurePath)
                        } else {
                            alert(`[featurePath] key is missing for challenge ${challenge.id}.`)
                        }
                    }
                }}
                id={`challenge-${challenge.id}`}
                variant="outlined"
                sx={{
                    cursor: 'pointer',
                    borderColor: progress ? (isClaimable ? 'secondary.main' : 'primary.main') : 'common.white',
                    '& .reward, .reward-asset, button': {
                        transition: 'all 0.55s ease-in-out',
                        transitionDelay: '0.15s',
                    },

                    '&:hover': {
                        button: {
                            filter: (theme) => `drop-shadow(0px 0px 5px ${alpha(theme.palette.secondary.main, 1)})`,
                        },
                        backgroundColor: (theme) => alpha(theme.palette[color].main, 0.1),
                        transform: 'scale(1.02)',
                        borderColor: `${color}.main`,
                        '.reward': {
                            borderColor: `${color}.main`,
                            backgroundColor: (theme) => alpha(theme.palette[color].main, 0.25),
                            borderRadius: { xs: '8px', md: '12px' },
                            transform: { xs: 'none', md: 'scale(120%)' },
                            borderTopLeftRadius: { xs: '8px', md: '0px' },
                            borderBottomLeftRadius: { xs: '8px', md: '0px' },
                        },
                        '.reward-asset': {
                            transform: 'scale(1.05)',
                            filter: (theme) => `drop-shadow(0px 0px 5px ${alpha(theme.palette[color].main, 1)})`,
                        },
                        '.title, .description': {
                            textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
                        },
                        '.description': {
                            transition: 'none',
                            fontWeight: 'bold',
                            color: 'secondary.main',
                            textDecoration: 'underline',
                            filter: (theme) => `drop-shadow(0px 0px 2px ${alpha(theme.palette.secondary.main, 0.75)})`,
                            textDecorationColor: (theme) => theme.palette.secondary.main,
                            textDecorationThickness: '2px',
                            textUnderlineOffset: '4px',
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        width: '100%',
                        gap: 1,
                        gridTemplateRows: '1fr',
                        gridTemplateColumns: '1fr auto',
                        gridTemplateAreas: {
                            xs: `"title title icon" 
                            "progress progress button"`,
                            md: `"title status icon" 
                            "progress button icon"`,
                        },
                    }}
                >
                    <Box
                        sx={{
                            gridArea: 'title',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            flexGrow: 1,
                            gap: 1,
                        }}
                    >
                        <Box>
                            <Typography className="title" variant={headerTypographyVariant} textTransform="uppercase">
                                {title}
                            </Typography>
                            <Typography
                                className="description"
                                textTransform={isMobile ? 'none' : 'uppercase'}
                                variant="body2"
                            >
                                {description}
                            </Typography>
                        </Box>
                        {isMobile && (
                            <Typography textAlign="end" variant="body2">
                                {timeLeft}
                            </Typography>
                        )}
                    </Box>

                    {!isMobile && (
                        <Box
                            sx={{
                                width: { sx: 'unset', md: '150px' },
                                gridArea: 'status',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: { xs: 'flex-end', md: progress ? 'space-between' : 'flex-end' },
                                'p, h5, h6': {
                                    textAlign: { xs: 'end', md: 'center' },
                                },
                            }}
                        >
                            {Boolean(progress) && (
                                <Typography
                                    textTransform="uppercase"
                                    variant={headerTypographyVariant}
                                    color={isClaimable ? 'secondary' : 'primary'}
                                >
                                    {isClaimable ? 'Completed' : 'In Progress'}
                                </Typography>
                            )}
                            <Typography textTransform="uppercase" fontWeight={'bold'} variant="body2">
                                {timeLeft}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ gridArea: 'progress' }}>
                        <LinearProgress
                            size={isMobile ? 'small' : 'medium'}
                            variant="determinate"
                            color={isClaimable ? 'secondary' : 'primary'}
                            value={normalise(progress, threshold)}
                            meter
                        />
                    </Box>

                    <Reward challenge={challenge} isClaimable={isClaimable} />
                    {isClaimable ? (
                        <Button
                            size={isMobile ? 'small' : 'medium'}
                            variant="contained"
                            color="secondary"
                            fullWidth
                            // onClick={handleClaim}
                            sx={{ gridArea: 'button', minWidth: 'unset', padding: 0 }}
                        >
                            Claim
                        </Button>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                gridArea: 'button',
                                textAlign: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: { xs: '18px', md: '24px' },
                                fontWeight: 'bold',
                            }}
                        >
                            {progress}/{threshold}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Slide>
    )
}
